import React, { useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { GenerateCode, getOrCreatePlayerId } from "../../common/game/basic";
import { SendRequest } from "../../util/AxiosUtil";
import { createMeeting } from "../../util/VideoSDK";
import MembershipPlanTable from "../../components/Tables/MembershipPlanTable";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../../slice/authSlice";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LogRocket from "../../util/LogRocketUtil";
import { validateCreateForm, validateHomeJoinForm } from "../../util/homeValidation";

const Home = () => {
  const navigator = useNavigate();
  const location = useLocation();
  const host_name_ref = useRef(null);
  const invitation_code_ref = useRef(null);
  const create_user_name_ref = useRef(null);
  const join_user_name_ref = useRef(null);
  const game_code_ref = useRef(null);
  const [recurring, setRecurring] = useState(false);
  const [onlyInvitees, setOnlyInvitees] = useState(false);
  const [videoChatAllow, setVideoChatAllow] = useState(false);
  const [copyButtonVisible, setCopyButtonVisible] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  // DC-FE-HOME-JOIN-UI-VALIDATION: Error state for Create and Join forms
  const [createErrors, setCreateErrors] = useState({});
  // DC-FE-HOME-JOIN-MAINFAIL-TDD-GREEN-001: Initialize joinErrors from location.state.joinApiError if present
  const [joinErrors, setJoinErrors] = useState(() => {
    const apiError = location?.state?.joinApiError;
    return apiError ? { apiError } : {};
  });

  const auth = useSelector((state) => state.auth);

  const dispatch = useDispatch();

  const copyInvitation = useCallback(() => {
    /* Select the text field */
    invitation_code_ref.current.select();
    invitation_code_ref.current.setSelectionRange(0, 99999);

    /* Copy the text inside the text field */
    window.navigator.clipboard.writeText(invitation_code_ref.current.value);
    setCopyButtonVisible(false);
    /* Alert the copied text */
  }, [invitation_code_ref]);

  //Create invitation code function
  const handleCreate = useCallback(() => {
    // DC-FE-HOME-JOIN-VALIDATION: Use inline validation instead of alert()
    if (!auth.isAuthorized) {
      const displayName = create_user_name_ref.current?.value || "";
      const errors = validateCreateForm({ displayName });
      if (Object.keys(errors).length > 0) {
        setCreateErrors(errors);
        return;
      }
      setCreateErrors({});
    }
    if (auth.isAuthorized && host_name_ref.current.value.trim().length === 0) {
      setCreateErrors({ displayName: "Host name is required." });
      return;
    }
    invitation_code_ref.current.value = GenerateCode();
    setCopyButtonVisible(true);
  }, [auth]);

  const handleStart = useCallback(async () => {
    console.log("=== handleStart clicked ===", {
      isAuthorized: auth.isAuthorized,
      userName: create_user_name_ref.current?.value,
      hostName: host_name_ref.current?.value,
      invitationCode: invitation_code_ref.current?.value,
    });

    // DC-FE-HOME-JOIN-UI-VALIDATION: Use validation helper for inline errors
    const displayName = create_user_name_ref.current?.value || "";
    const errors = validateCreateForm({ displayName });
    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      return;
    }
    setCreateErrors({});

    if (
      auth.isAuthorized &&
      host_name_ref.current.value.trim(" ").length === 0
    ) {
      // Keep this validation for authorized users (host name)
      setCreateErrors({ displayName: "Host name is required." });
      return;
    }

    if (invitation_code_ref.current.value.trim(" ").length === 0) {
      // Keep this validation - invitation code must be generated
      setCreateErrors({ displayName: "Generate Invitation code first." });
      return;
    }

    if (
      !auth.isAuthorized &&
      displayName.trim().length > 15
    ) {
      setCreateErrors({ displayName: "Name must be less than 15 letters." });
      return;
    }

    // Get or create per-browser player identity
    const playerId = getOrCreatePlayerId();

    // Update Redux with playerId and display name for use in Main.js
    dispatch(
      setUser({
        Id: playerId,
        DisplayName: displayName,
        NickName: displayName,
      })
    );

    let HostName = auth.isAuthorized ? host_name_ref.current.value : "";
    let GameCode = invitation_code_ref.current.value;
    setIsCreating(true);

    //video chat allowed
    let meetingId = null;
    if (videoChatAllow) {
      meetingId = await createMeeting();
      if (meetingId === null) {
        alert("Can't Create Video Meeting.");
        setIsCreating(false);
        return;
      }
    } else meetingId = null;
    // Use the typed "UserName" field for player display name
    const realDisplayName = displayName;

    console.log("=== Calling CreateGame API ===", {
      playerId,
      realDisplayName,
      GameCode,
      HostName,
    });

    SendRequest({
      method: "post",
      url: "api/Games/CreateGame",
      data: {
        HostName: HostName,
        MeetingId: meetingId,
        UserId: playerId,
        DisplayName: realDisplayName,
        GameCode: GameCode,
        IsRecurring: recurring,
        IsInvitesOnly: onlyInvitees,
        VideoChatAllowed: videoChatAllow,
      },
    }).then((result) => {
      console.log("=== CreateGame result ===", result);
      if (result.data) {
        let link = "/game/main-game?GameCode=" + GameCode;
        if (meetingId !== null && meetingId !== undefined)
          link += `&MeetingId=${meetingId}`;
        navigator(link);
      } else {
        console.warn("CreateGame returned falsy data:", result.data);
      }
    }).catch((err) => {
      console.error("=== CreateGame error ===", err);
      alert("Failed to create game. Check console for details.");
      setIsCreating(false);
    });
  }, [auth, dispatch, navigator, onlyInvitees, recurring, videoChatAllow]);

  const JoinGame = useCallback(() => {
    // DC-FE-HOME-JOIN-UI-VALIDATION: Use validation helper for inline errors
    const displayName = join_user_name_ref.current?.value || "";
    const gameCode = game_code_ref.current?.value || "";
    const errors = validateHomeJoinForm({ displayName, gameCode });
    if (Object.keys(errors).length > 0) {
      setJoinErrors(errors);
      return;
    }
    setJoinErrors({});

    // Additional check for name length (not covered by base validation)
    if (!auth.isAuthorized && displayName.trim().length > 15) {
      setJoinErrors({ displayName: "Name must be less than 15 letters." });
      return;
    }
    
    // Get or create per-browser player identity
    const playerId = getOrCreatePlayerId();
    
    // Update Redux with playerId and display name for use in Main.js
    dispatch(
      setUser({
        Id: playerId,
        DisplayName: displayName,
        NickName: displayName,
      })
    );

    LogRocket.identify(playerId, {
      name: displayName,
    });
    
    let link = "/game/main-game?GameCode=" + game_code_ref.current.value;
    console.log("[Home.js JoinGame] Calling api/Games/GetMeetinId with GameCode:", game_code_ref.current.value);
    SendRequest({
      url: "api/Games/GetMeetinId",
      method: "post",
      data: {
        GameCode: game_code_ref.current.value,
      },
    }).then((result) => {
      console.log("[Home.js JoinGame] GetMeetinId response:", result.data);
      // DC-FE-GETMEETINID-NULL-TDD-GREEN-001: New semantics
      // - 200 OK with data === null means "game exists, no video chat"
      // - 200 OK with data === string means "game exists, has video chat"
      // - 404 / error (catch) means "game not found"
      const meetingId = result?.data;
      
      // Only append MeetingId if it exists (non-null, non-empty)
      if (meetingId) {
        link += `&MeetingId=${meetingId}`;
      }
      
      // Clear any previous joinErrors and navigate
      setJoinErrors({});
      navigator(link);
    }).catch((err) => {
      // DC-FE-GETMEETINID-NULL-TDD-GREEN-001: 404 or network error = game not found
      console.error("[Home.js JoinGame] GetMeetinId error:", err);
      setJoinErrors({ apiError: "Unable to join this game. The game may not exist, be locked, or you may not be invited." });
    });
  }, [auth, dispatch, navigator]);

  //function App() {
  //    return (
  //        <div className='container'>
  //            <h2>Hello useState</h2></div>
  //    );
  //}

  //start
  if (!isCreating)
    return (
      <div className="container p-2">
        <div className="row banner my-auto">
          <div className="col-6 text-center">
            <h1>DealersChoice.club</h1>
            <h3>Your game. Your rules. Your table.</h3>
          </div>
        </div>
        <div className="row create-join mt-5 justify-content-between g-3">
          <div className="col-12 m-2 MainPageDivTitle">
            <h2>Play for free</h2>
          </div>
          <div className="col-md-6 MainPageDivs">
            <h2>HOST: Create a Game</h2>
            <div className="form-group mt-2">
              <label htmlFor="UserName">Your Name at Table</label>
              <input
                autoComplete="off"
                ref={create_user_name_ref}
                type="text"
                className="ms-2 form-control d-inline w-50"
                placeholder="Your name please..."
                id="UserName"
                name="UserName"
                maxLength={10}
                onChange={(e) => {
                  // DC-FE-HOME-JOIN-VALIDATION: Enforce 10 char limit via JS for test compatibility
                  if (e.target.value.length > 10) {
                    e.target.value = e.target.value.slice(0, 10);
                  }
                }}
                required
              />
              {createErrors.displayName && (
                <div className="text-danger mt-1">{createErrors.displayName}</div>
              )}
            </div>
            <div className="form-group mt-2">
              <label htmlFor="GameName">Game/Table Name</label>
              <input
                autoComplete="off"
                ref={host_name_ref}
                type="text"
                className="ms-2 form-control d-inline w-50"
                id="GameName"
                placeholder="Game name"
                name="GameName"
                required
              />
              <button className="btn btn-primary ms-2" onClick={handleCreate}>
                Create
              </button>
            </div>
            <div className="form-group">
              <div className="d-flex m-2 text-center"></div>
              <div>
                <label htmlFor="pwd">Invitation Code:</label>
                <input
                  autoComplete="off"
                  type="text"
                  className="ms-2 form-control w-50 d-inline"
                  id="GameCode"
                  placeholder="Code to share"
                  name="GameCode"
                  ref={invitation_code_ref}
                  required
                  readOnly
                />
                <button className="ms-2 btn">
                  {copyButtonVisible ? (
                    <ContentCopyIcon onClick={copyInvitation} />
                  ) : (
                    <CheckCircleOutlineIcon />
                  )}
                </button>
              </div>
            </div>
            {auth.isAuthorized && (
              <>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox onChange={() => setRecurring(!recurring)} />
                    }
                    label="Create as recurring game"
                  />
                </FormGroup>
                <FormGroup>
                  <FormControlLabel
                    checked={onlyInvitees}
                    control={
                      <Checkbox
                        onChange={() => setOnlyInvitees(!onlyInvitees)}
                      />
                    }
                    label="Only Invitees can join this game"
                  />
                </FormGroup>
                <FormGroup>
                  <FormControlLabel
                    checked={videoChatAllow}
                    control={
                      <Checkbox
                        onChange={() => setVideoChatAllow(!videoChatAllow)}
                      />
                    }
                    label="Allow Video Chat"
                  />
                </FormGroup>
              </>
            )}
            &nbsp;
            <div className="text-center">
              <button
                type="submit"
                className="btn btn-primary mb-2 dc-home-cta"
                onClick={handleStart}
              >
                Start Game
              </button>
              <br />
            </div>
          </div>
          <div className="col-md-6 MainPageDivs">
            <h2>GUEST: Join a Game</h2>
            <div className="form-group mb-2">
              <label htmlFor="JoinUserName">Your Name at Table</label>
              <input
                autoComplete="off"
                ref={join_user_name_ref}
                type="text"
                className="ms-2 form-control d-inline w-50"
                placeholder="Your name please..."
                id="JoinUserName"
                name="JoinUserName"
                required
              />
              {joinErrors.displayName && (
                <div className="text-danger mt-1">{joinErrors.displayName}</div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="GameCode">Game Code</label>
              <input
                autoComplete="off"
                ref={game_code_ref}
                type="text"
                className="ms-2 form-control d-inline w-50"
                placeholder="Game Code"
                name="GameCode"
                required
              />
              {joinErrors.gameCode && (
                <div className="text-danger mt-1">{joinErrors.gameCode}</div>
              )}
            </div>
            <div className="text-center">
              <button
                type="submit"
                className="btn btn-primary mt-2 mb-2 dc-home-cta"
                onClick={JoinGame}
              >
                Join
              </button>
              {/* DC-FE-JOIN-PREFLIGHT-001: Inline error for API join failures */}
              {/* US-008: Transform DUPLICATE_DISPLAY_NAME token into friendly message */}
              {joinErrors.apiError && (
                <div className="text-danger mt-2" role="alert">
                  {joinErrors.apiError === "DUPLICATE_DISPLAY_NAME"
                    ? "That name is already taken. Please choose a different name."
                    : joinErrors.apiError}
                </div>
              )}
              <br />
            </div>
          </div>
        </div>
        <div className="row features mt-5">
          <div className="col-12 text-center mb-3">
            <h2>Members can</h2>
          </div>
          <div className="row text-center g-4">
            <div className="col-6 col-md-3">
              <i className="bi bi-people fs-1 text-primary"></i>
              <h3>Save guest lists</h3>
            </div>
            <div className="col-6 col-md-3">
              <i className="bi bi-calendar-event fs-1 text-primary"></i>
              <h3>Schedule games</h3>
            </div>
            <div className="col-6 col-md-3">
              <i className="bi bi-clock-history fs-1 text-primary"></i>
              <h3>Save game history</h3>
            </div>
            <div className="col-6 col-md-3">
              <i className="bi bi-star fs-1 text-primary"></i>
              <h3>
                Access premium features like ad-free games, in-game video,
                and more!
              </h3>
            </div>
          </div>
          <div className="col-12 text-center mt-4">
            <a href="/auth/sign-up" className="join-btn btn fs-2 px-4 py-2">
              Join Now<span className="fs-5 d-block">for free!</span>
            </a>
          </div>
        </div>
        {/* Membership section: show for anonymous users OR free members (MembershipPlanId === 1) */}
        {(!auth.asset || auth.asset.MembershipPlanId === 1) && (
          <div className="row mt-5" data-testid="membership-section">
            <div className="col-12 text-center mb-3">
              <h2>Membership</h2>
            </div>
            <MembershipPlanTable />
          </div>
        )}
      </div>
    );
  return <img src="assets/images/loading.gif" alt="" />;
};

export default Home;
